import { motion } from "framer-motion";
import { useTeacherNotices } from "../../hooks/useData";
import { formatPLN, formatDate, statusColor } from "../../lib/finance";

function NoticeCard({ notice, index }) {
  const isPast = notice.due_date && new Date(notice.due_date) < new Date();
  const isPaid = notice.status === "zapłacone" || notice.status === "paid";

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07 }}
      className={`card p-6 space-y-4 relative overflow-hidden ${!isPaid && !isPast ? "border-amber-400/20" : ""}`}
    >
      {!isPaid && !isPast && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="font-semibold text-slate-100">{notice.title}</h3>
          {notice.description && (
            <p className="text-sm text-slate-400 mt-1">{notice.description}</p>
          )}
        </div>
        <span className={`badge ${statusColor(notice.status)} flex-shrink-0`}>
          {notice.status ?? "Aktywne"}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-3 border-t border-navy-700">
        {notice.amount_per_child != null && (
          <div>
            <div className="text-xs text-slate-500 mb-1">Kwota za dziecko</div>
            <div className="font-semibold text-gold-400 font-mono">{formatPLN(notice.amount_per_child)}</div>
          </div>
        )}
        {notice.due_date && (
          <div>
            <div className="text-xs text-slate-500 mb-1">Termin</div>
            <div className={`font-medium text-sm ${isPast && !isPaid ? "text-red-400" : "text-slate-300"}`}>
              {isPast && !isPaid && "⚠ "}{formatDate(notice.due_date)}
            </div>
          </div>
        )}
        {notice.total_amount != null && (
          <div>
            <div className="text-xs text-slate-500 mb-1">Łączna kwota</div>
            <div className="font-semibold text-slate-200 font-mono">{formatPLN(notice.total_amount)}</div>
          </div>
        )}
      </div>

      {notice.notes && (
        <div className="bg-navy-800 rounded-xl px-4 py-3 text-xs text-slate-400 border border-navy-700">
          {notice.notes}
        </div>
      )}
    </motion.div>
  );
}

export default function NoticesScreen() {
  const { data: notices, loading } = useTeacherNotices();

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const active = notices.filter((n) => n.status !== "zapłacone" && n.status !== "paid" && n.status !== "Anulowane");
  const done = notices.filter((n) => n.status === "zapłacone" || n.status === "paid");

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <p className="text-xs text-gold-400 uppercase tracking-widest font-medium mb-1">Informacje</p>
        <h1 className="font-display text-3xl font-semibold text-slate-100">Przypomnienia</h1>
        <div className="gold-line mt-2" />
        <p className="text-sm text-slate-500 mt-3">Powiadomienia od wychowawcy dotyczące opłat i terminów</p>
      </motion.div>

      {active.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-sm font-medium text-slate-400 uppercase tracking-widest">Aktywne ({active.length})</h2>
          {active.map((n, i) => <NoticeCard key={n.id} notice={n} index={i} />)}
        </section>
      )}

      {done.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-sm font-medium text-slate-400 uppercase tracking-widest">Zakończone ({done.length})</h2>
          {done.map((n, i) => <NoticeCard key={n.id} notice={n} index={i} />)}
        </section>
      )}

      {notices.length === 0 && (
        <div className="card p-12 text-center text-slate-500">Brak przypomnień</div>
      )}
    </div>
  );
}
